import Konva from 'konva';
import type {
  BlockedSeat,
  BlockedSeats,
  Position,
  SeatState2,
  SeattixState,
  SelectedSeat,
  SelectedSeats,
} from './types';
import { Stand2 } from './stand';
import { Block2 } from './block';
import type { Seat2 } from './seat';
import type { IRect } from 'konva/lib/types';
import { isMobile } from './common';

const MAX_SCALE = 2;
const MIN_SCALE = 0.5;

const MOBILE_MIN_SCALE = MIN_SCALE - 0.2;
const MOBILE_MAX_SCALE = MAX_SCALE - 0.5;

const ZOOM_IN_BLOCK = 2.5;
const ZOOM_IN_BLOCK_SMALL = 1.2;

export type TixTicketType = {
  mapId: string;
  name: string;
  price: string;
};

export type TixState = {
  ticketTypes: TixTicketType[];
};

export type SeatAvailability = 'available' | 'occupied' | 'sold' | 'selected';

export type ViewerOptions = {
  containerId: string;
  state: SeattixState;
  tixState: TixState;
};

export class Viewer {
  private stage: Konva.Stage;
  private state: SeattixState;
  private layer: Konva.Layer;
  private baseX: number;
  private baseY: number;
  private selectedSeats: SelectedSeats;
  private blockedSeats: BlockedSeats;
  private tooltip: HTMLElement;
  private confirmSelectBtn: HTMLButtonElement;
  private readonly containerEl: HTMLElement;
  private blocks: Block2[];
  private selectingSeat?: {
    block: Block2;
    tt: TixTicketType;
    seat: Seat2;
  } | null;
  private readonly tixState: TixState;
  private onSelectedSeatsChangeCallback!: (
    seat: SelectedSeat,
    selected: boolean
  ) => void;

  constructor(opts: ViewerOptions) {
    const el = document.getElementById(opts.containerId);
    if (!el) {
      throw new Error(
        `Editor container with id '${opts.containerId}' was not found.`
      );
    }

    this.containerEl = el;

    this.tooltip = document.getElementById('seatTooltip')!;
    this.confirmSelectBtn = document.getElementById(
      'tooltipConfirmBtn'
    )! as HTMLButtonElement;

    this.confirmSelectBtn.addEventListener('click', () => {
      this.onPressConfirmSelection();
    });

    const closeTooltipBtn = document.getElementById('tooltipCloseBtn');

    closeTooltipBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.hideTooltip();
    });

    document.addEventListener('click', () => {
      this.hideTooltip();
    });

    this.tixState = opts.tixState;
    this.blockedSeats = {};
    this.blocks = [];
    this.state = opts.state;
    this.stage = new Konva.Stage({
      container: opts.containerId,
      width: el.offsetWidth,
      height: el.offsetHeight,
    });

    document.addEventListener('click', () => {
      this.hideTooltip();
    });

    this.stage.addEventListener('click', () => {
      this.hideTooltip();
    });

    this.baseX = this.stage.width() / 2;
    this.baseY = this.stage.height() / 2;

    this.layer = new Konva.Layer({});
    this.stage.add(this.layer);

    this.selectedSeats = {};
    this.stage.draggable(true);

    this.onWheel();
    this.onMobileWheel();
    this.onDragStage();
  }

  public show() {
    this.layer.destroyChildren();
    this.loadBlocks();
    this.loadStands();
    this.fitStageIntoParentContainer();
  }

  public hide() {
    this.layer.destroyChildren();
  }

  public unblockSeat(blockId: string, seat: BlockedSeat) {
    const blockedSeats = this.blockedSeats[blockId] || [];
    if (!blockedSeats) return;

    const indx = blockedSeats.findIndex(
      (s) => s.rowId === seat.rowId && s.seatId === seat.seatId
    );

    if (indx > -1) {
      blockedSeats.splice(indx, 1);
    }

    const block = this.blocks.find((b) => b.nodeId === blockId);
    if (!block) {
      console.warn('block was not found. trying to block seats');
      return;
    }

    const row = block.rows.find((r) => r.nodeId === seat.rowId);
    let seats: Seat2[];
    if (row) {
      seats = row.seats;
    } else {
      const table = block.tables.find((t) => t.nodeId === seat.rowId);
      if (!table) {
        console.warn('nor row or table was not found. trying to block seats');
        return;
      }

      seats = table.seats;
    }

    const foundSeat = seats.find((s) => s.seatNumber === +seat.seatId);
    if (!foundSeat) {
      console.warn('seat was not found. trying to seat seats');
      return;
    }

    const state = foundSeat.currentState;
    const originalState = (state as any).originalState;
    state.id = originalState.id;
    state.stroke = originalState.stroke;
    state.strokeWidth = originalState.strokeWidth;
    state.fillText = originalState.fillText;
    state.fill = originalState.fill;

    this.removeSelectedSeat(block.nodeId, foundSeat.nodeId);
    foundSeat.update();
  }

  public getBlockColor(blockId: string) {
    const b = this.blocks.find((b) => b.nodeId === blockId);
    return b?.currentState?.groupColor || b?.currentState?.fill;
  }

  public getBlockGroup(blockId: string) {
    const b = this.blocks.find((b) => b.nodeId === blockId);
    return b?.currentState?.group;
  }

  public blockSeats(blockId: string, seats: BlockedSeat[]) {
    const blockedSeats = this.blockedSeats[blockId] || [];
    if (!blockedSeats) return;
    seats.forEach((seat) => {
      const alreadyBlocked = blockedSeats.find(
        (s) => s.rowId === seat.rowId && s.seatId === seat.seatId
      );

      if (!alreadyBlocked) {
        blockedSeats.push(seat);
      }

      const block = this.blocks.find((b) => b.nodeId === blockId);
      if (!block) {
        console.warn('block was not found. trying to block seats');
        return;
      }

      const row = block.rows.find((r) => r.nodeId === seat.rowId);
      let seats: Seat2[];
      if (row) {
        seats = row.seats;
      } else {
        const table = block.tables.find((t) => t.nodeId === seat.rowId);
        if (!table) {
          console.warn('nor row or table was not found. trying to block seats');
          return;
        }

        seats = table.seats;
      }

      const foundSeat = seats.find((s) => s.seatNumber === +seat.seatId);
      if (!foundSeat) {
        console.warn('seat was not found. trying to block seats');
        return;
      }

      const state = foundSeat.currentState;
      if (!(state as any).originalState) {
        (state as any).originalState = { ...state };
      }

      state.stroke = 'black';
      state.fill = 'black';
      state.fillText = 'black';
      state.strokeWidth = 0;

      foundSeat.update();
    });

    this.blockedSeats[blockId] = blockedSeats;
  }

  public onSelectedSeatsChange(
    callback: typeof this.onSelectedSeatsChangeCallback
  ) {
    this.onSelectedSeatsChangeCallback = callback;
  }

  private loadBlocks() {
    this.blocks = [];
    this.state.blocks.forEach((state, index) => {
      this.selectedSeats[state.id!] = [];

      const tixBlock = this.tixState.ticketTypes.find(
        (t) => t.mapId === state.id
      );

      let label = tixBlock?.name || state.label;
      state.label = label;

      const position = state.position || { x: this.baseX, y: this.baseY };
      const block = new Block2(this.layer, {
        ...state,
        position,
        draggable: false,
      });

      block.onHover(() => {
        document.body.style.cursor = 'pointer';
      });

      block.onHoverOut(() => {
        document.body.style.cursor = 'auto';
      });

      this.onBlockPressHandler(block);

      block.create();

      block.rows.forEach((row) => {
        this.forEachSeat(row.seats, tixBlock!, block);
      });

      block.tables.forEach((table) => {
        table.onHover(() => {
          document.body.style.cursor = 'pointer';
        });

        table.onHoverOut(() => {
          document.body.style.cursor = 'auto';
        });

        this.forEachSeat(table.seats, tixBlock!, block);
      });

      this.state.blocks[index].id = block.nodeId;
      this.blocks.push(block);
    });

    for (const blockId in this.blockedSeats) {
      const seats = this.blockedSeats[blockId];
      this.blockSeats(blockId, seats);
    }
  }

  private onBlockPressHandler(block: Block2) {
    block.onPress((_, type, obj, evt) => {
      switch (type) {
        case 'row':
          break;
        case 'seat':
          this._onSeatPressHandler(evt, block, obj as Seat2);
          break;
        case 'block':
          break;
      }

      if (type !== 'seat') {
        this.hideTooltip();
      }
    });
  }

  private _onSeatPressHandler(evt: any, block: Block2, seat: Seat2) {
    const state = seat.currentState;
    const isSelected = this.isSeatSelected(block.nodeId, seat.nodeId);

    if (!(state as any).originalState) {
      (state as any).originalState = { ...state };
    }

    const isOccupied =
      this.getSeatAvailability(
        block.nodeId,
        seat.seatNumber.toString(),
        seat.rowId
      ) === 'occupied';
    if (isOccupied) return;

    const tt = this.tixState.ticketTypes.find(
      (tt) => tt.mapId === block.nodeId
    )!;

    const selectedSeat = this.buildSelectedSeat(seat, block, tt!);

    if (isMobile() && !isSelected) {
      const tixBlock = this.tixState.ticketTypes.find(
        (t) => t.mapId === block.nodeId
      );

      this.tooltip.style.position = 'fixed';
      this.tooltip.style.left = '50%';
      this.tooltip.style.top = '50%';
      this.tooltip.style.transform = 'translate(-50%, -50%)';


      this.selectingSeat = { seat, block, tt };
      this.showTooltip(tixBlock!, block, seat);

      return;
    }

    if (isSelected) {
      const originalState = (state as any).originalState;
      state.id = originalState.id;
      state.stroke = originalState.stroke;
      state.strokeWidth = originalState.strokeWidth;
      state.fillText = originalState.fillText;
      state.fill = originalState.fill;

      this.removeSelectedSeat(block.nodeId, seat.nodeId);
    } else {
      state.stroke = 'black';
      state.fillText = '#fff';
      state.strokeWidth = 1;
      state.fill = '#28a745';
      this.selectedSeats[block.nodeId].push(selectedSeat);
    }

    this.onSelectedSeatsChangeCallback(selectedSeat, !isSelected);
    seat.update();
  }

  private loadStands() {
    this.state.stands.forEach((state, i) => {
      const position = state.position || { x: this.baseX, y: this.baseY };
      const stand = new Stand2(this.layer, {
        ...state,
        position,
        id: state.id!,
        draggable: false,
      });

      stand.create();

      stand.onStateChange((newState) => {
        this.state.stands[i] = newState;
      });
    });
  }

  private onWheel() {
    const stage = this.stage;
    stage.on('wheel', (e: any) => {
      e.evt.preventDefault();

      const scaleBy = 1.05;
      const oldScale = stage.scaleX();
      const pointer = stage.getPointerPosition();

      const mousePointTo = {
        x: (pointer!.x - stage.x()) / oldScale,
        y: (pointer!.y - stage.y()) / oldScale,
      };

      let newScale = Math.max(
        e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy,
        MIN_SCALE
      );

      newScale = Math.min(newScale, MAX_SCALE);

      stage.scale({ x: newScale, y: newScale });

      const newPos = {
        x: pointer!.x - mousePointTo.x * newScale,
        y: pointer!.y - mousePointTo.y * newScale,
      };

      stage.position(newPos);
    });
  }

  private onMobileWheel() {
    Konva.hitOnDragEnabled = true;
    const stage = this.stage;
    let lastCenter: Position | null = null;
    let lastDist = 0;
    let dragStopped = false;

    const getDistance = (p1: Position, p2: Position) => {
      return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
    };

    const getCenter = (p1: Position, p2: Position) => {
      return {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2,
      };
    };

    stage.on('touchmove', function (e: any) {
      e.evt.preventDefault();
      const touch1 = e.evt.touches[0];
      const touch2 = e.evt.touches[1];

      // we need to restore dragging, if it was cancelled by multi-touch
      if (touch1 && !touch2 && !stage.isDragging() && dragStopped) {
        stage.startDrag();
        dragStopped = false;
      }

      if (touch1 && touch2) {
        // if the stage was under Konva's drag&drop
        // we need to stop it, and implement our own pan logic with two pointers
        if (stage.isDragging()) {
          dragStopped = true;
          stage.stopDrag();
        }

        const rect = stage.container().getBoundingClientRect();

        const p1 = {
          x: touch1.clientX - rect.left,
          y: touch1.clientY - rect.top,
        };
        const p2 = {
          x: touch2.clientX - rect.left,
          y: touch2.clientY - rect.top,
        };

        if (!lastCenter) {
          lastCenter = getCenter(p1, p2);
          return;
        }
        const newCenter = getCenter(p1, p2);

        const dist = getDistance(p1, p2);

        if (!lastDist) {
          lastDist = dist;
        }

        // local coordinates of center point
        const pointTo = {
          x: (newCenter.x - stage.x()) / stage.scaleX(),
          y: (newCenter.y - stage.y()) / stage.scaleX(),
        };

        let scale = Math.max(
          stage.scaleX() * (dist / lastDist),
          MOBILE_MIN_SCALE
        );
        scale = Math.min(scale, MOBILE_MAX_SCALE);

        stage.scaleX(scale);
        stage.scaleY(scale);

        // calculate new position of the stage
        const dx = newCenter.x - lastCenter.x;
        const dy = newCenter.y - lastCenter.y;

        const newPos = {
          x: newCenter.x - pointTo.x * scale + dx,
          y: newCenter.y - pointTo.y * scale + dy,
        };

        stage.position(newPos);

        lastDist = dist;
        lastCenter = newCenter;
      }
    });

    stage.on('touchend', function () {
      lastDist = 0;
      lastCenter = null;
    });
  }

  private isSeatSelected(blockId: string, seatId: string) {
    const seats = this.selectedSeats[blockId];
    if (!seats || seats.length === 0) return false;

    return seats.some((s) => s.seatId === seatId);
  }

  private getSeatAvailability(
    blockId: string,
    seatId: string,
    rowId: string
  ): SeatAvailability {
    if (this.selectedSeats[blockId].find((s) => s.seatId === seatId)) {
      return 'selected';
    } else if (
      this.blockedSeats[blockId] &&
      this.blockedSeats[blockId].find(
        (s) => s.rowId === rowId && s.seatId === seatId
      )
    ) {
      return 'occupied';
    } else {
      return 'available';
    }
  }

  private onDragStage() {
    this.stage.on('dragmove', () => {
      const scale = this.stage.scaleX();
      return;

      const boxes = this.layer.children.map((b: any) => b.getClientRect());
      const box = this._getTotalBox(boxes);
      console.log('BOXY: ', box.y);

      this.layer.children.forEach((shape: any) => {
        const absPos = shape.getAbsolutePosition();
        const offsetX = box.x - absPos.x;
        const offsetY = box.y - absPos.y;

        const newAbsPos = { ...absPos };

        if (box.x < 0) {
          newAbsPos.x = -offsetX;
        }

        if (box.y < 0) {
          newAbsPos.y = -offsetY;
        }

        if (box.x + box.width > this.stage.width()) {
          newAbsPos.x = this.stage.width() - box.width - offsetX;
        }

        if (box.y + box.height > this.stage.height()) {
          newAbsPos.y = this.stage.height() - box.height - offsetY;
        }

        shape.setAbsolutePosition(newAbsPos);
      });
    });
  }

  private _getTotalBox(boxes: IRect[]) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    boxes.forEach((box) => {
      minX = Math.min(minX, box.x);

      minY = Math.min(minY, box.y);
      maxX = Math.max(maxX, box.x + box.width);
      maxY = Math.max(maxY, box.y + box.height);
    });

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }

  private showTooltip(tixBlock: TixTicketType, block: Block2, seat: Seat2) {
    const state = block.currentState;
    const price = tixBlock?.price ? (+tixBlock.price).toLocaleString() : '0';

    let sectionLabel = state.label || '';
    if (sectionLabel!.length > 20) {
      sectionLabel = sectionLabel?.slice(0, 20) + '...';
    }

    const isPremium = /VIP|PREMIUM|FRONT\s*STAGE/i.test(state.label || '');
    this.tooltip.classList.toggle('premium', isPremium);

    const sectionPrice = price;
    const availabilityTexts: Record<SeatAvailability, string> = {
      available: 'Disponible',
      occupied: 'Ocupado',
      selected: 'Seleccionado',
      sold: 'Vendido',
    };

    document.body.style.cursor = 'pointer';

    this.tooltip.classList.add('show');
    this.tooltip.style.zIndex = '99999';
    document.getElementById('tooltipLabel')!.textContent =
      state.tables && state.tables.length ? 'Mesa:' : 'Fila:';
    document.getElementById('tooltipSection')!.textContent = sectionLabel;
    document.getElementById('tooltipPrice')!.textContent = `RD$${sectionPrice}`;
    document.getElementById('tooltipRow')!.textContent = seat.rowLabel || '';
    document.getElementById('tooltipSeat')!.textContent =
      seat.currentState.label;

    const availabilityEl = document.getElementById('tooltipAvailability')!;

    const availability = this.getSeatAvailability(
      block.nodeId,
      seat.seatNumber.toString(),
      seat.rowId
    );

    const text = availabilityTexts[availability];
    availabilityEl.className = `tooltip-availability ${availability}`;
    availabilityEl.innerHTML = `
                    <span class="availability-dot"></span>
                    ${text}
                `;

    if (isMobile()) {
      this.confirmSelectBtn.classList.remove('hidden');
    }
  }

  private hideTooltip() {
    this.selectingSeat = null;
    this.tooltip.style.zIndex = '-1';
    this.tooltip.classList.remove('show');
  }

  private buildSelectedSeat(
    seat: Seat2,
    block: Block2,
    tt: TixTicketType
  ): SelectedSeat {
    return {
      blockId: block.nodeId,
      rowId: seat.rowId,
      seatId: seat.nodeId,
      seatNumber: seat.seatNumber,
      price: tt?.price ? +tt.price : 0,
      row: seat.rowLabel,
      previewText: `${block.currentState.label} | ${block.tables && block.tables.length ? 'Mesa ' : ''} ${seat.rowLabel}-${seat.currentState.label}`,
    };
  }

  private onPressConfirmSelection() {
    if (!this.selectingSeat) {
      return;
    }

    const { block, tt, seat } = this.selectingSeat;
    const blockId = block.nodeId;
    const state = seat.currentState;
    state.stroke = 'black';
    state.fillText = '#fff';
    state.strokeWidth = 1;
    state.fill = '#28a745';
    seat.update();

    const selectedSeat = this.buildSelectedSeat(seat, block, tt!);
    this.selectedSeats[blockId].push(selectedSeat);

    this.onSelectedSeatsChangeCallback(selectedSeat, true);
    this.hideTooltip();
    this.selectingSeat = null;
  }

  private fitStageIntoParentContainer() {
    const boxesTmp = this.layer.children.map((b: any) => b.getClientRect());
    const boxTmp = this._getTotalBox(boxesTmp);
    const screenWidth = this.containerEl.offsetWidth;
    let scale = Math.max(
      screenWidth / boxTmp.width -
        (isMobile()
          ? (this.state.scalePaddingMobile ?? 0)
          : (this.state.scalePadding ?? 0.05)),
      0.2
    );

    if (this.state.maxScale) {
      scale = Math.min(this.state.maxScale, scale);
    }

    //    if (clientWidth > 1400) {
    //      scale = 0.4;
    //    } else if (clientWidth > 1000) {
    //      scale = 0.7;
    //    } else if (clientWidth > 900) {
    //      scale = 0.6;
    //    } else if (clientWidth <= 900 && clientWidth >= 678) {
    //      scale = 0.5;
    //    } else if (clientWidth <= 678) {
    //      scale = 0.3;
    //    }

    this.stage.scale({ x: scale, y: scale });

    const boxes = this.layer.children.map((b: any) => b.getClientRect());
    const box = this._getTotalBox(boxes);

    const stageWidth = this.stage.width() / scale;
    const stageHeight = this.stage.height() / scale;
    const boxWidth = box.width / scale;
    const boxHeight = box.height / scale;

    const boxX = stageWidth / 2 - boxWidth / 2;
    const boxY = stageHeight / 2 - boxHeight / 2;

    const currentBoxX = box.x / scale;
    const currentBoxY = box.y / scale;

    const offsetX = boxX - currentBoxX;
    const offsetY = boxY - currentBoxY;

    this.layer.children.forEach((shape: any) => {
      const pos = shape.position();
      shape.position({
        x: pos.x + offsetX,
        y: pos.y + offsetY,
      });
    });
  }

  private forEachSeat(seats: Seat2[], tixBlock: TixTicketType, block: Block2) {
    seats.forEach((seat) => {
      seat.onHover(() => {
        this.showTooltip(tixBlock, block, seat);
      });

      seat.onHoverOut(() => {
        this.hideTooltip();
        document.body.style.cursor = 'auto';
      });

      seat.onMouseMove((e) => {
        const offset = 10;

        let topY = e.evt.pageY + offset;
        let x = e.evt.pageX + offset;
        const clientWidth =
          window.innerWidth ||
          document.documentElement.clientWidth ||
          document.body.clientWidth;

        const clientHeight =
          window.innerHeight ||
          document.documentElement.clientHeight ||
          document.body.clientHeight;

        if (topY + this.tooltip.offsetHeight > clientHeight) {
          topY = topY - this.tooltip.offsetHeight;
        }

        if (x + this.tooltip.offsetWidth > clientWidth) {
          x = x - this.tooltip.offsetWidth;
        }

        this.tooltip.style.left = x + 'px';
        this.tooltip.style.top = topY + 'px';
      });
    });
  }

  private removeSelectedSeat(blockId: string, seatId: string) {
    const indx = this.selectedSeats[blockId].findIndex(
      (s) => s.seatId === seatId
    );

    if (indx > -1) this.selectedSeats[blockId].splice(indx, 1);
  }
}
